import { useEffect, useRef } from "react";

const loadScript = (src) =>
  new Promise((resolve) => {
    const base = src.split("?")[0];
    if ([...document.scripts].some((s) => s.src.startsWith(base))) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.defer = true; s.onload = resolve;
    document.body.appendChild(s);
  });

// Loads an invisible reCAPTCHA v3 script and returns a fresh token for the given action.
export async function execRecaptchaV3(siteKey, action = "signup") {
  await loadScript(`https://www.google.com/recaptcha/api.js?render=${siteKey}`);
  await new Promise((res) => window.grecaptcha.ready(res));
  return window.grecaptcha.execute(siteKey, { action });
}

export default function CaptchaField({ provider, siteKey, onToken }) {
  const boxRef = useRef(null);

  useEffect(() => {
    if (!provider || provider === "none" || provider === "recaptcha_v3" || !siteKey) return;
    let cancelled = false;

    const render = (api) => {
      if (cancelled || !boxRef.current || boxRef.current.hasChildNodes()) return;
      api.render(boxRef.current, {
        sitekey: siteKey,
        callback: (t) => onToken(t),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      });
    };

    if (provider === "recaptcha_v2") {
      loadScript("https://www.google.com/recaptcha/api.js?render=explicit").then(() => {
        const tick = () => {
          if (cancelled) return;
          if (window.grecaptcha && window.grecaptcha.render) render(window.grecaptcha);
          else setTimeout(tick, 200);
        };
        tick();
      });
    } else if (provider === "turnstile") {
      loadScript("https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit").then(() => {
        const tick = () => {
          if (cancelled) return;
          if (window.turnstile) render(window.turnstile);
          else setTimeout(tick, 200);
        };
        tick();
      });
    }

    return () => { cancelled = true; };
  }, [provider, siteKey, onToken]);

  if (!provider || provider === "none" || provider === "recaptcha_v3") return null;
  return <div ref={boxRef} data-testid="captcha-widget" className="min-h-[66px]" />;
}
